import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Paper, Typography, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid,
  Card, CardContent, CardActions, useMediaQuery, useTheme,
  Chip, Autocomplete, TextField, CircularProgress,
  Checkbox, FormControlLabel
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { workersApi } from '../services/api';

const ProjectWorkersTab = ({ projectId, workers, refreshWorkers, currentUser }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const showProjectHours = currentUser?.role === 'Office'
    && workers.some((worker) => typeof worker.project_hours === 'number');
  const totalProjectHours = showProjectHours
    ? workers.reduce((sum, worker) => sum + (Number(worker.project_hours) || 0), 0)
    : 0;

  const formatHours = (hours) => {
    const numericHours = Number(hours) || 0;
    return Number.isInteger(numericHours) ? `${numericHours}` : numericHours.toFixed(2);
  };
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [allWorkers, setAllWorkers] = useState([]);
  const [isAddLoading, setIsAddLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    worker_id: '',
    selectedWorker: null,
    do_not_notify: false,
  });
  
  // Note: Roles are no longer assigned per project - they come from the worker's profile
  
  useEffect(() => {
    // Fetch all available workers when component mounts
    const fetchWorkers = async () => {
      try {
        const response = await workersApi.getAll();
        setAllWorkers(response.data);
      } catch (error) {
        console.error('Error fetching workers:', error);
      }
    };
    
    fetchWorkers();
  }, []);
  
  // Mobile worker card component
  const WorkerCard = ({ worker }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            {worker.name}
          </Typography>
          <Chip 
            label={worker.role}
            color="primary"
            size="small"
            variant="outlined"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {worker.email}
        </Typography>
        {showProjectHours && (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Project Hours: {formatHours(worker.project_hours)}
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => handleOpenDeleteDialog(worker)}
          disabled={isDeleteLoading}
          fullWidth
        >
          Remove from Project
        </Button>
      </CardActions>
    </Card>
  );

  const handleOpenDialog = () => {
    setFormData({
      worker_id: '',
      selectedWorker: null,
      do_not_notify: false,
    });
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  const handleOpenDeleteDialog = (worker) => {
    setSelectedWorker(worker);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleWorkerSelect = (event, newValue) => {
    setFormData({
      ...formData,
      selectedWorker: newValue,
      worker_id: newValue ? newValue.worker_id : '',
    });
  };

  const handleDoNotNotifyChange = (event) => {
    setFormData({
      ...formData,
      do_not_notify: event.target.checked,
    });
  };
  
  const handleSubmit = async () => {
    setIsAddLoading(true);
    try {
      await workersApi.assignToProject(projectId, formData);
      handleCloseDialog();
      refreshWorkers();
    } catch (error) {
      console.error('Error assigning worker to project:', error);
    } finally {
      setIsAddLoading(false);
    }
  };
  
  const handleDelete = async () => {
    setIsDeleteLoading(true);
    try {
      await workersApi.removeFromProject(projectId, selectedWorker.worker_id);
      handleCloseDeleteDialog();
      refreshWorkers();
    } catch (error) {
      console.error('Error removing worker from project:', error);
    } finally {
      setIsDeleteLoading(false);
    }
  };
  
  // Desktop columns
  const columns = [
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'role', headerName: 'Role', width: 200 },
    ...(showProjectHours ? [{
      field: 'project_hours',
      headerName: 'Project Hours',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatHours(params.value),
    }] : []),
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => handleOpenDeleteDialog(params.row)}
          disabled={isDeleteLoading}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Filter out workers already assigned to the project
  const availableWorkers = allWorkers.filter(
    worker => !workers.some(projectWorker => projectWorker.worker_id === worker.worker_id)
  );

  return (
    <Box sx={{ height: '100%', width: '100%', p: { xs: 1, md: 2 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: 2,
        gap: 2
      }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          disabled={availableWorkers.length === 0}
          fullWidth={isMobile}
        >
          Add Team Member
        </Button>
      </Box>
      
      {/* Mobile view - Cards */}
      {isMobile ? (
        <Box>
          {workers.map((worker) => (
            <WorkerCard key={worker.worker_id} worker={worker} />
          ))}
          {showProjectHours && workers.length > 0 && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Total Project Hours: {formatHours(totalProjectHours)}
              </Typography>
            </Paper>
          )}
          {workers.length === 0 && (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No team members assigned to this project
              </Typography>
            </Paper>
          )}
        </Box>
      ) : (
        /* Desktop view - DataGrid */
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={workers}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              getRowId={(row) => row.worker_id}
              disableSelectionOnClick
            />
          </Box>
          {showProjectHours && workers.length > 0 && (
            <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', textAlign: 'right' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Total Project Hours: {formatHours(totalProjectHours)}
              </Typography>
            </Box>
          )}
        </Paper>
      )}
      
      {/* Add Worker Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Assign Team Member to Project</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={availableWorkers}
                getOptionLabel={(option) => `${option.name} (${option.email}) - ${option.role}`}
                value={formData.selectedWorker}
                onChange={handleWorkerSelect}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search and select team member"
                    required
                    placeholder="Type to search team members..."
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1" component="div">
                        {option.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email} • {option.role}
                      </Typography>
                    </Box>
                  </Box>
                )}
                isOptionEqualToValue={(option, value) => option.worker_id === value.worker_id}
                noOptionsText="No team members available"
                clearOnBlur
                selectOnFocus
                handleHomeEndKeys
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={formData.do_not_notify}
                    onChange={handleDoNotNotifyChange}
                  />
                )}
                label="Do not notify"
              />
              <Typography variant="body2" color="text.secondary">
                The team member will not receive the initial project assignment notification. Future project notifications will still be sent.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
          <Button onClick={handleCloseDialog} fullWidth={isMobile} disabled={isAddLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!formData.worker_id || isAddLoading}
            fullWidth={isMobile}
          >
            {isAddLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                Assigning...
              </Box>
            ) : (
              'Assign'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCloseDeleteDialog}
        fullScreen={isMobile}
      >
        <DialogTitle>Confirm Remove</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {selectedWorker?.name} from this project?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
          <Button onClick={handleCloseDeleteDialog} fullWidth={isMobile} disabled={isDeleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={isDeleteLoading}
            fullWidth={isMobile}
          >
            {isDeleteLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                Removing...
              </Box>
            ) : (
              'Remove'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectWorkersTab;
